import './services.cookie-consent';
import './services.ga';
import './services.product';
import { isEmpty, isNil, omitBy, sumBy, isString } from 'lodash-es';
import {
  EVENT_NAME_FOR_PAGE_VIEW,
  EVENT_NAME_FOR_PRODUCT_LIST,
  TRACKER_GENERAL_EVENT_TYPES,
  USER_ACTION_TYPES,
  EXPERIMENT_START_FOR_AB_TESTING,
  MENU_TRACKER_TYPE,
} from '../constants/tracker';

export function trackerService(
  $rootScope,
  $location,
  mainConfig,
  $filter,
  Analytics,
  productService,
  fbPixelSettings,
  gaService,
  slFeatureService,
  cookieConsentService,
  $http,
) {
  const userActionTypes = USER_ACTION_TYPES;
  const menuTrackerType = MENU_TRACKER_TYPE;

  const fbContentType = function (fbPixelSetting) {
    return fbPixelSetting && fbPixelSetting.single_variation
      ? 'product_group'
      : 'product';
  };

  const fbInitiateCheckout = function ({ items, subtotal, eventID }) {
    if (!eventID) return;
    try {
      // facebook tracking pixel Standard Event: initiateCheckout
      if (subtotal === undefined || subtotal.currency_iso === '') return;

      const eventName = 'InitiateCheckout';

      fbq(
        'track',
        eventName,
        {
          content_type: fbContentType(fbPixelSettings[0]),
          content_ids: _.map(items, function (item) {
            return productService.getSku(
              item.product_id,
              item.product.sku,
              item.variation,
              fbPixelSettings[0],
            );
          }),
          num_items: sumBy(items, 'quantity'),
          value: subtotal.dollars || 0,
          currency: subtotal.currency_iso,
        },
        {
          eventID,
        },
      );
    } catch (e) {
      // facebook tracking pixel doesn't exist
    }
  };

  const getAIData = (action, eventCategory) => {
    const validAIDataTypes = [
      userActionTypes.ADD_ITEM,
      userActionTypes.UPDATE_ITEM,
      userActionTypes.REMOVE_ITEM,
    ];

    if (!validAIDataTypes.includes(action) || eventCategory !== 'Cart') {
      return undefined;
    }

    const aiDataFromUrl = $location.search().ai_data;

    if (!isEmpty(aiDataFromUrl)) {
      return aiDataFromUrl;
    }

    const referrerSearchUrl = document.referrer.split('?')[1] || '';
    const urlSearch = new URLSearchParams(referrerSearchUrl);
    const aiDataFromReferrer = urlSearch.get('ai_data');

    if (!isEmpty(aiDataFromReferrer)) {
      return aiDataFromReferrer;
    }

    return undefined;
  };

  const generalEventType = TRACKER_GENERAL_EVENT_TYPES;

  const mapEventType = (eventName) => {
    switch (eventName) {
      case EVENT_NAME_FOR_PRODUCT_LIST.ProductClick:
        return generalEventType.PRODUCT_CLICK;
      case EVENT_NAME_FOR_PRODUCT_LIST.WishlistAdd:
        return generalEventType.ADD_TO_WISHLIST;
      case EVENT_NAME_FOR_PRODUCT_LIST.WishlistRemove:
        return generalEventType.REMOVE_FROM_WISHLIST;
      default:
        return null;
    }
  };

  const getTrackerScope = () =>
    mainConfig.pageType === 'one_page_stores-show' ? 'one_page_store' : 'shop';

  $rootScope.$on(generalEventType.ADD_TO_CART, (e, { items, eventId }) => {
    try {
      const totalValue = items.reduce(
        (accumulator, item) => accumulator + (item.value || 0),
        0,
      );
      // facebook tracking pixel Standard Event: addToCart
      fbq(
        'track',
        'AddToCart',
        {
          content_name: $filter('translateModel')(
            items[0].product.title_translations,
          ),
          content_ids: items.map((item) =>
            productService.getSku(
              item.product._id,
              item.product.sku,
              item.variationSelected,
              fbPixelSettings[0],
            ),
          ),
          content_type: fbContentType(fbPixelSettings[0]),
          value: totalValue,
          currency: mainConfig.merchantData.base_currency_code,
        },
        {
          eventID: eventId,
        },
      );
    } catch (e) {
      // facebook tracking pixel doesn't exist
    }
  });

  return {
    track: function (event, props) {
      const [eventName, eventData] = !isString(event)
        ? [event.type, event.data]
        : [event, props];

      $rootScope.$broadcast(eventName, eventData);

      const otherEventName = mapEventType(eventName);
      if (otherEventName) {
        $rootScope.$broadcast(otherEventName, eventData);
      }
    },
    shouldSendDefaultPageView: () =>
      ![
        'products-show',
        'orders-confirm',
        'checkout-index',
        'fast_checkout-index',
      ].includes(mainConfig.pageType),
    shouldSendQuickCartPageView: function () {
      return mainConfig.pageType === 'one_page_stores-show';
    },
    // TODO: Move into unified tracker management
    pageView: function (props = {}) {
      const { data: propsData = {}, ...restProps } = props;
      const { 'Product ID': product_id, 'Category ID': category_id } =
        mainConfig.trackerEventData ?? {};
      const pageViewData = {
        scope: getTrackerScope(),
        ...restProps,
        user_id: mainConfig.currentUser?._id ?? null,
        data: JSON.stringify({
          ...propsData,
          ...(product_id && { product_id }),
          ...(category_id && { category_id }),
        }),
      };

      // Shoplytics tracker will only be loaded after obtaining user consent
      if (typeof shoplytics === 'function') {
        shoplytics('track', EVENT_NAME_FOR_PAGE_VIEW.PageView, pageViewData);
      }
    },
    // TODO: Move into unified tracker management
    productImpression: function (props = {}) {
      const { page_type, product_ids } = props;
      const payload = {
        user_id: mainConfig.currentUser?._id ?? null,
        data: JSON.stringify({
          page_type,
          product_ids,
        }),
      };

      // Shoplytics tracker will only be loaded after obtaining user consent
      if (typeof shoplytics === 'function') {
        shoplytics(
          'track',
          EVENT_NAME_FOR_PRODUCT_LIST.ProductImpression,
          payload,
        );
      }
    },
    userAction: function (eventName, eventCategory, data = {}) {
      const {
        type,
        productId,
        variationId,
        quantity,
        price,
        rec_strategy,
        scope,
        ...restData
      } = data || {};

      const eventPayload = {
        event_name: eventName,
        event_category: eventCategory,
        user_id: mainConfig.currentUser?._id ?? null,
        data: JSON.stringify({
          ...restData,
          type: type ?? null,
          product_id:
            productId || mainConfig.trackerEventData?.['Product ID'] || null,
          variation_id: variationId ?? null,
          quantity: quantity ?? null,
          price: price ?? null,
          ai_data: getAIData(eventName, eventCategory),
          ...(rec_strategy && {
            rec_strategy,
          }),
        }),
        ...(scope && {
          scope,
        }),
      };

      // Shoplytics tracker will only be loaded after obtaining user consent
      if (typeof shoplytics === 'function') {
        shoplytics('track', eventName, eventPayload);
      }
    },
    gaSendDetails: function (product, variation, related_products) {
      // Analytics.addProduct(productId, name, category, brand, variant, price, quantity, coupon, position);
      if (Analytics.configuration.enhancedEcommerce) {
        gaService.setUserId();
        if (related_products != null) {
          var ga_temp_variation = null;
          for (var index = related_products.length - 1; index >= 0; --index) {
            ga_temp_variation = null;
            if (
              related_products[index]['variations'] != null &&
              related_products[index]['variations'].length > 0
            ) {
              ga_temp_variation = related_products[index]['variations'][0];
            }
            // values marked "" are either for phase2, or not marked as required on GA examples
            Analytics.addImpression(
              productService.getSku(
                related_products[index]['id'],
                related_products[index]['sku'],
                ga_temp_variation,
              ),
              $filter('translateModel')(
                related_products[index]['title_translations'],
              ),
              '',
              '',
              '',
              productService.getVariationName(ga_temp_variation),
              '0',
              '',
            );
          }
        }
        Analytics.addProduct(
          productService.getSku(product._id, product.sku, variation),
          $filter('translateModel')(product.title_translations),
          '',
          '',
          productService.getVariationName(variation),
          '',
          '',
          '',
          '0',
        );
        Analytics.trackDetail();
      }
    },

    fbSendViewContent: async function (product, value, variationSelected) {
      try {
        // facebook tracking pixel Standard Event: ViewContent
        var eventId = [
          'ViewContent',
          new Date().getTime(),
          mainConfig.currentUser
            ? mainConfig.currentUser._id
            : nanoidNonSecure(),
        ].join('.');

        if (slFeatureService.hasFeature('fb_ssapi')) {
          const trackingData = {
            event_id: eventId,
            fbc: cookieConsentService.getConsentedCookie('marketing', '_fbc'),
            fbp: cookieConsentService.getConsentedCookie('marketing', '_fbp'),
            product_id: product._id,
            product_sku: product.sku,
            product_name: $filter('translateModel')(product.title_translations),
            variation_key: variationSelected?.key,
            variation_sku: variationSelected?.sku,
            value,
            external_id: mainConfig.currentUser?._id,
            fn: mainConfig.currentUser?.name
              ? forge_sha256(mainConfig.currentUser.name)
              : undefined,
            em: mainConfig.currentUser?.email
              ? forge_sha256(mainConfig.currentUser.email)
              : undefined,
            ph: mainConfig.currentUser?.phone
              ? forge_sha256(mainConfig.currentUser.phone)
              : undefined,
            event_source_url: window.location.href,
          };

          $http.get('/api/track/fb_view_content', {
            params: omitBy(trackingData, isNil),
          });
        }

        fbq(
          'track',
          'ViewContent',
          {
            content_type: fbContentType(fbPixelSettings[0]),
            content_ids: [
              productService.getSku(
                product._id,
                product.sku,
                variationSelected,
                fbPixelSettings[0],
              ),
            ],
            content_name: $filter('translateModel')(product.title_translations),
            currency: mainConfig.merchantData.base_currency_code,
            value: value,
          },
          {
            eventID: eventId,
          },
        );
      } catch (e) {
        // facebook pixel does not exist
      }
    },

    addToWishlist: function (product, value, variationSelected) {
      try {
        // facebook tracking pixel Standard Event: AddToWishlist
        fbq('track', 'AddToWishlist', {
          value: value,
          currency: mainConfig.merchantData.base_currency_code,
          content_name: $filter('translateModel')(product.title_translations),
          content_ids: [
            productService.getSku(
              product._id,
              product.sku,
              variationSelected,
              fbPixelSettings[0],
            ),
          ],
          content_type: fbContentType(fbPixelSettings[0]),
        });
      } catch (e) {
        // facebook tracking pixel doesn't exist
      }
      this.track('WishlistAdd', {
        product_id: productService.getSku(
          product._id,
          product.sku,
          variationSelected,
        ),
        value,
      });
    },

    removeFromWishlist: function (product) {
      this.track('WishlistRemove', {
        product_id: product._id,
      });
    },

    fbPurchase: function (order) {
      try {
        // facebook tracking pixel Standard Event: purchase
        fbq(
          'track',
          'Purchase',
          {
            content_type: fbContentType(fbPixelSettings[0]),
            content_ids: _.map(order.subtotal_items, function (item) {
              return productService.getSku(
                item.object_data._id,
                item.object_data.sku,
                item.item_data.variation_data,
                fbPixelSettings[0],
              );
            }),
            num_items: sumBy(order.subtotal_items, 'quantity'),
            value: order.total.dollars,
            currency: order.total.currency_iso,
            order_id: order.order_number,
          },
          {
            eventID: 'Purchase.' + order._id,
          },
        );
      } catch (e) {
        // facebook tracking pixel doesn't exist
      }
    },

    fbInitiateCheckout: function (items, subtotal) {
      const eventID =
        'InitiateCheckout' +
        '.' +
        String(Math.floor(Math.random() * 100000 + 1)) +
        '.' +
        String(Math.floor(Date.now()));
      cookieConsentService.putConsentedCookie(
        'marketing',
        'fb_initiate_checkout_event_id',
        eventID,
      );

      fbInitiateCheckout({ items, subtotal, eventID });
    },

    fbInitiateCheckoutForCartLink: function (items, subtotal) {
      const eventID = cookieConsentService.getConsentedCookie(
        'marketing',
        'cart_link_fb_initiate_checkout_event_id',
      );
      fbInitiateCheckout({ items, subtotal, eventID });

      cookieConsentService.removeConsentedCookie(
        'marketing',
        'cart_link_fb_initiate_checkout_event_id',
      );
    },
    fbAddPaymentInfo: function (items, total) {
      try {
        // facebook tracking pixel Standard Event: AddPaymentInfo
        fbq('track', 'AddPaymentInfo', {
          content_type: fbContentType(fbPixelSettings[0]),
          content_ids: _.map(items, function (item) {
            return productService.getSku(
              item.product_id,
              item.product.sku,
              item.variation,
              fbPixelSettings[0],
            );
          }),
          num_items: sumBy(items, 'quantity'),
          value: total.dollars,
          currency: total.currency_iso,
        });
      } catch (e) {
        // facebook tracking pixel doesn't exist
      }
    },
    fbSignup: function (id) {
      try {
        //facebook tracking pixel Standard Event: Lead
        fbq(
          'track',
          'Lead',
          {
            content_name: id,
          },
          {
            eventID: 'Lead.' + id,
          },
        );
      } catch (e) {
        // facebook tracking pixel doesn't exist
      }
    },
    generalEventType,
    menuTrackerType,
    userActionTypes,
    getTrackerScope,
    experimentStart: function (trackerData, event_category) {
      shoplytics('track', EXPERIMENT_START_FOR_AB_TESTING, {
        event_category,
        data: JSON.stringify(trackerData),
        user_id: mainConfig.currentUser?.['_id'],
      });
    },
  };
}

app.service('trackerService', [
  '$rootScope',
  '$location',
  'mainConfig',
  '$filter',
  'Analytics',
  'productService',
  'fbPixelSettings',
  'gaService',
  'slFeatureService',
  'cookieConsentService',
  '$http',
  trackerService,
]);
